import React, { useState, useEffect, useRef } from 'react';
import { node } from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3';
import { publicRuntimeConfig } from '../../../config';
import { listProducts } from '../../../services/marketplace';
import { buildListProductsDateFilters, mapMarketplaceOrders } from './marketplaceHelpers';
import {
    CustomLogo,
    FlexContainer,
    IsMobileWrapper,
    Main,
    RootContainer,
    Row,
    Text,
    Notification,
    If,
    NotFound
} from '../../common';
import PinSection from './components/PinSection';
import OrderReceptionModal from './components/OrderReceptionModal';

// Configuración para el servicio de command-products
const { recaptchaToken, commandOrdersUrl, commandOrdersApiKey } = publicRuntimeConfig.settings;

// Secret key para desencriptar el mktStore
const ENCRYPTION_SECRET = 'OvsrT2ipQumDJQS6BJNMqAQy+VQi9wcB+mzA9h7N/uE=';

// ponytail: react-recaptcha-v3 hace grecaptcha.execute en su lifecycle sin try/catch; con la
// sitekey inválida (p.ej. localhost) tira síncrono y, sin error boundary, React desmonta toda
// la página (pantalla en blanco). Este boundary aísla ese fallo para no romper la vista.
class RecaptchaBoundary extends React.Component {
    state = { failed: false };
    static getDerivedStateFromError () { return { failed: true }; }
    render () { return this.state.failed ? null : this.props.children; }
}

RecaptchaBoundary.propTypes = { children: node };

const Marketplace = () => {
    // Obtener storeId y mktStore encriptado desde la URL
    // Ahora con ruta: /marketplace/:storeId/*
    // Ejemplo: /marketplace/J780M/U2FsdGVkX1+8jGcJhW1mrlrP/phbLhM+tspDUfaTa1AI=
    const getParamsFromUrl = () => {
        const pathname = window.location.pathname;
        const parts = pathname.split('/').filter(part => part !== '');

        // parts[0] = 'marketplace'
        // parts[1] = storeId (ej: 'J780M')
        // parts[2...] = todo el resto es el encryptedMktStore (puede tener /)
        const storeId = parts[1] || null;

        // Tomar todo lo que viene después de 'marketplace/storeId/'
        // y unirlo de nuevo con '/' para reconstruir el string completo
        const encryptedMktStore = parts.length > 2
            ? parts.slice(2).join('/')
            : null;

        let sellerId = null;
        let hasDecryptionError = false;

        if (encryptedMktStore) {
            try {
                const decrypted = CryptoJS.AES.decrypt(
                    decodeURIComponent(encryptedMktStore),
                    ENCRYPTION_SECRET
                );
                const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

                if (decryptedString && decryptedString.trim().length > 0) {
                    sellerId = decryptedString;
                } else {
                    hasDecryptionError = true;
                }
            } catch (error) {
                hasDecryptionError = true;
            }
        } else {
            hasDecryptionError = true;
        }

        return { storeId, sellerId, hasDecryptionError };
    };

    const { storeId, sellerId, hasDecryptionError } = getParamsFromUrl();
    const showError = hasDecryptionError;

    // Filtro de fechas (default hoy -> hoy + 3 días). storeId sigue viniendo de la URL.
    const defaultFrom = moment().startOf('day');
    const defaultTo = moment().add(3, 'days').endOf('day');
    const [dateFrom, setDateFrom] = useState(defaultFrom);
    const [dateTo, setDateTo] = useState(defaultTo);
    const [dateResetKey, setDateResetKey] = useState(0);

    // Ref con los últimos filtros para el refresco automático (evita closures rancios)
    const paramsRef = useRef({ dateFrom: defaultFrom, dateTo: defaultTo });
    useEffect(() => { paramsRef.current = { dateFrom, dateTo }; });

    const [pin, setPin] = useState('');
    const [orderData, setOrderData] = useState(null);
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState(null);
    const [receivedQuantities, setReceivedQuantities] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [productsData, setProductsData] = useState(null);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('fromPin'); // 'fromPin' | 'fromEye'
    const [, setSelectedOrderFromTable] = useState(null);

    useEffect(() => {
        if (showError) {
            return;
        }

        loadReCaptcha(recaptchaToken);
        // Esperar a que reCAPTCHA se cargue y verificar que tengamos los parámetros
        if (storeId && sellerId) {
            setTimeout(() => {
                fetchProducts(dateFrom, dateTo);
            }, 1000);
        }

        // Refresco automático cada 1 minuto (60000 ms) con los últimos filtros
        const intervalId = setInterval(() => {
            const { dateFrom: f, dateTo: t } = paramsRef.current;
            if (storeId && sellerId && window.grecaptcha) {
                fetchProducts(f, t);
            }
        }, 60000);

        // Limpiar el intervalo cuando el componente se desmonte
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    const fetchProducts = async (fromArg = dateFrom, toArg = dateTo) => {
        if (!window.grecaptcha) {
            return;
        }

        if (!storeId || !sellerId) {
            return;
        }

        setLoadingProducts(true);
        try {
            const response = await listProducts({
                storeId,
                sellerId,
                statusPin: 1,
                isDeleted: false,
                ...buildListProductsDateFilters(fromArg, toArg)
            });

            if (response.internalCode === 200 && response.payload) {
                setProductsData(mapMarketplaceOrders(response.payload, storeId));
            }
        } catch (error) {
        } finally {
            setLoadingProducts(false);
        }
    };

    const validatePin = async (skipViewChange = false, setErrorCallback = setError) => {
        setErrorCallback(null);
        setShowSuccess(false);
        setLoading(true);

        try {
            return await new Promise((resolve) => {
                window.grecaptcha.ready(async () => {
                    try {
                        const token = await window.grecaptcha.execute(recaptchaToken, { action: 'submit' });

                        const { data } = await axios.request({
                            method: 'post',
                            url: `${commandOrdersUrl}/command/validatePin`,
                            headers: {
                                'Content-Type': 'application/json',
                                'x-api-key': commandOrdersApiKey,
                                'x-recaptcha-token': token,
                                'isfront': true
                            },
                            data: { pin }
                        });

                        if (data.internalCode === 200 && data.payload) {
                            const responseData = data.payload;

                            // Validar que tenga productos
                            if (!responseData.products || responseData.products.length === 0) {
                                setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                                if (!skipViewChange) {
                                    setOrderData(null);
                                }
                                setLoading(false);
                                resolve(false);
                                return;
                            }

                            // Mapear productos con IDs únicos para el manejo de cantidades
                            const productsWithIds = responseData.products.map((product, index) => ({
                                ...product,
                                id: `${responseData.orderId}-${index}` // ID único basado en orden e índice
                            }));

                            // Crear objeto de orden con estructura esperada por el componente
                            const order = {
                                orderId: responseData.orderId,
                                seller: responseData.mktStore,
                                products: productsWithIds
                            };

                            // Inicializar cantidades disponibles en 0 (el seller las ingresa)
                            const initialQuantities = {};
                            productsWithIds.forEach(product => {
                                initialQuantities[product.id] = 0;
                            });

                            if (!skipViewChange) {
                                setOrderData(order);
                                setReceivedQuantities(initialQuantities);
                            }

                            setLoading(false);
                            resolve(true);
                        } else {
                            setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                            if (!skipViewChange) {
                                setOrderData(null);
                            }
                            setLoading(false);
                            resolve(false);
                        }
                    } catch (error) {
                        console.error('Error validating pin:', error);
                        setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                        if (!skipViewChange) {
                            setOrderData(null);
                        }
                        setLoading(false);
                        resolve(false);
                    }
                });
            });
        } catch (error) {
            console.error('Error validating pin:', error);
            setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
            if (!skipViewChange) {
                setOrderData(null);
            }
            setLoading(false);
            return false;
        }
    };

    const handleQuantityChange = (productId, value, maxQuantity) => {
        // Si está vacío, establecer como 0
        if (value === '' || value === null || value === undefined) {
            setReceivedQuantities({
                ...receivedQuantities,
                [productId]: 0
            });
            return;
        }

        // Remover ceros a la izquierda y convertir
        const cleanValue = value.replace(/^0+/, '') || '0';
        const numValue = parseInt(cleanValue, 10);

        if (isNaN(numValue) || numValue < 0) {
            return;
        }

        const finalValue = numValue > maxQuantity ? maxQuantity : numValue;
        setReceivedQuantities({
            ...receivedQuantities,
            [productId]: finalValue
        });
    };

    const handleValidateAndOpenModal = async () => {
        const isValid = await validatePin(false);
        if (isValid) {
            openModalFromPin();
        }
    };

    const openModalFromPin = () => {
        setModalVisible(true);
        setModalMode('fromPin');
        setModalError(null);
    };

    const openModalFromEye = (order) => {
        setModalVisible(true);
        setModalMode('fromEye');
        setSelectedOrderFromTable(order);
        setPin('');

        // Mapear productos de la orden seleccionada con IDs únicos
        const productsWithIds = order.products.map((product, index) => ({
            ...product,
            id: product.id || `${order.orderId}-${index}`,
            name: product.nameComplete || product.name,
            requestedQuantity: product.quantityOriginal || product.requestedQuantity
        }));

        // Crear objeto de orden con estructura esperada
        const formattedOrder = {
            orderId: order.orderId,
            seller: order.products[0]?.mktStore || '',
            products: productsWithIds
        };

        // Inicializar cantidades en 0
        const initialQuantities = {};
        productsWithIds.forEach(product => {
            initialQuantities[product.id] = 0;
        });

        setOrderData(formattedOrder);
        setReceivedQuantities(initialQuantities);
        setModalError(null);
        setShowSuccess(false);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedOrderFromTable(null);
        setModalError(null);
        setShowSuccess(false);
        // Si el modal se abrió desde el PIN, limpiar también orderData
        if (modalMode === 'fromPin') {
            setOrderData(null);
            setReceivedQuantities({});
        }
    };

    const handleModalSubmit = async () => {
        // Validaciones
        if (!pin) {
            setModalError('Por favor, ingresa el PIN para confirmar.');
            return;
        }

        // Si viene del ícono del ojo, primero validar el PIN
        if (modalMode === 'fromEye') {
            const isValid = await validatePin(true, setModalError);
            if (!isValid) {
                return; // El error ya fue seteado en validatePin
            }
        }

        // Llamar al servicio de confirmación
        await handleSubmit();
    };

    const getErrorMessageByCode = (internalCode, defaultMessage) => {
        // Mapeo de códigos internos a mensajes controlados por el frontend
        const errorMessages = {
            404: 'El PIN no fue encontrado o ya ha sido utilizado anteriormente.',
            400: 'Los datos enviados no son válidos. Por favor, verifica la información.',
            401: 'No tienes autorización para realizar esta acción.',
            403: 'Acceso denegado. Contacta al administrador.',
            500: 'Ocurrió un error en el servidor. Intenta nuevamente en unos momentos.',
            503: 'El servicio no está disponible temporalmente. Intenta más tarde.'
        };

        return errorMessages[internalCode] || defaultMessage || 'No se pudo completar la operación. Por favor, intenta nuevamente.';
    };

    const getErrorMessage = (error) => {
        // Convertir errores técnicos a mensajes amigables
        const errorString = error?.message || error || '';

        if (errorString.includes('404') || errorString.includes('not found')) {
            return 'No se encontró la información solicitada. Verifica que los datos sean correctos.';
        }
        if (errorString.includes('401') || errorString.includes('403') || errorString.includes('unauthorized')) {
            return 'No tienes autorización para realizar esta acción. Contacta al administrador.';
        }
        if (errorString.includes('500') || errorString.includes('Internal Server Error')) {
            return 'Estamos teniendo problemas temporales. Intenta nuevamente en unos momentos.';
        }
        if (errorString.includes('timeout') || errorString.includes('timed out')) {
            return 'La operación tardó demasiado tiempo. Verifica tu conexión e intenta nuevamente.';
        }
        if (errorString.includes('network') || errorString.includes('Network')) {
            return 'Hay un problema de conexión. Verifica tu red e intenta nuevamente.';
        }

        return errorString || 'No se pudo completar la operación. Por favor, intenta nuevamente.';
    };

    const handleSubmit = async () => {
        // Validaciones
        if (!pin) {
            setModalError('Por favor, ingresa el PIN para confirmar.');
            return;
        }

        setLoading(true);
        setModalError(null);

        try {
            await window.grecaptcha.ready(async () => {
                const token = await window.grecaptcha.execute(recaptchaToken, { action: 'submit' });

                // Mapear productos con sus cantidades disponibles
                const products = orderData.products.map(product => ({
                    ean: product.ean,
                    quantity: receivedQuantities[product.id] || 0
                }));

                const { data } = await axios.request({
                    method: 'post',
                    url: `${commandOrdersUrl}/command/confirmPicking`,
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': commandOrdersApiKey,
                        'x-recaptcha-token': token,
                        'isFront': 'true'
                    },
                    data: {
                        pin,
                        idOrder: orderData.orderId,
                        sellerId,
                        products
                    }
                });

                if (data.internalCode === 200) {
                    setShowSuccess(true);
                    setModalError(null);
                    // Refrescar la lista de pedidos pendientes
                    await fetchProducts();
                    // No redirigir automáticamente, mantener el formulario visible
                } else {
                    // Usar mapeo de códigos de error controlado por el frontend
                    setModalError(getErrorMessageByCode(data.internalCode, data.message));
                }

                setLoading(false);
            });
        } catch (error) {
            console.error('Error confirming picking: ', error);

            // Intentar extraer mensaje del error de axios
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.internalCode) {
                    setModalError(getErrorMessageByCode(errorData.internalCode, errorData.message));
                } else {
                    setModalError(getErrorMessage(error));
                }
            } else {
                setModalError(getErrorMessage(error));
            }

            setLoading(false);
        }
    };

    // El backend filtra por storeId (URL) + fecha; se pinta lo que vuelve.
    const handleDateFromChange = (selected) => {
        if (!selected) return;
        setDateFrom(selected);
        fetchProducts(selected, dateTo);
    };
    const handleDateToChange = (selected) => {
        if (!selected) return;
        setDateTo(selected);
        fetchProducts(dateFrom, selected);
    };

    const handleClearFilters = () => {
        setDateFrom(defaultFrom);
        setDateTo(defaultTo);
        setDateResetKey(k => k + 1);
        fetchProducts(defaultFrom, defaultTo);
    };

    return (
        <RootContainer>
            <Main background='F4F4F4'>
                {/* Si hay error de desencriptación, mostrar página de error */}
                {showError ? (
                    <NotFound
                        icon='caution'
                        iconBackgroundColor='#f38630'
                        iconColor='#FFFFFF'
                        firstTextLine='URL no válida'
                        secondTextLine='El enlace que estás intentando acceder no es válido. Por favor, verifica la URL e intenta nuevamente.'
                        hasGoBack={false}
                    />
                ) : (
                    <IsMobileWrapper>
                        {({ isMobile }) => {
                            return (
                                <>
                                    {/* Header Principal - Fuera de la tarjeta */}
                                    <FlexContainer padding='24px 0 0 0' direction='column'>
                                        <Row justify='center' padding={!isMobile ? 25 : 0}>
                                            <FlexContainer width='100%' justify='center'>
                                                <CustomLogo style={{ width: isMobile ? window.innerWidth * 0.5 : '195px', marginBottom: '18px' }} />
                                            </FlexContainer>
                                            <Text text='Preparación de Productos Marketplace' strong size={24} />
                                        </Row>
                                    </FlexContainer>

                                    {/* Vista inicial: Input PIN y Tabla de pedidos pendientes */}
                                    <PinSection
                                        storeName={sellerId}
                                        pin={pin}
                                        onChange={setPin}
                                        onValidate={handleValidateAndOpenModal}
                                        loading={loading}
                                        error={error}
                                        onErrorClose={() => setError(null)}
                                        pendingOrders={productsData}
                                        loadingProducts={loadingProducts}
                                        onOrderEyeClick={openModalFromEye}
                                        defaultFrom={defaultFrom}
                                        defaultTo={defaultTo}
                                        dateResetKey={dateResetKey}
                                        onDateFromChange={handleDateFromChange}
                                        onDateToChange={handleDateToChange}
                                        onClearFilters={handleClearFilters}
                                    />

                                    <RecaptchaBoundary>
                                        <ReCaptcha
                                            sitekey={recaptchaToken}
                                            action='submit'
                                        />
                                    </RecaptchaBoundary>

                                    {/* Success Notification */}
                                    <If
                                        condition={showSuccess && !modalVisible}
                                        then={
                                            <FlexContainer padding={18} justify='center'>
                                                <Notification
                                                    type='success'
                                                    message='✓ Entrega confirmada exitosamente. Los productos han sido registrados correctamente.'
                                                />
                                            </FlexContainer>
                                        }
                                    />

                                    {/* Modal de recepción */}
                                    <OrderReceptionModal
                                        visible={modalVisible}
                                        onClose={closeModal}
                                        order={orderData}
                                        pin={pin}
                                        onPinChange={setPin}
                                        isPinReadOnly={modalMode === 'fromPin'}
                                        quantities={receivedQuantities}
                                        onQuantityChange={handleQuantityChange}
                                        onSubmit={handleModalSubmit}
                                        loading={loading}
                                        error={modalError}
                                        showSuccess={showSuccess}
                                        onErrorClose={() => setModalError(null)}
                                        onSuccessClose={() => setShowSuccess(false)}
                                    />
                                </>
                            );
                        }}
                    </IsMobileWrapper>
                )}
            </Main>
        </RootContainer>
    );
};

export default Marketplace;
